import React, { useState, useEffect } from 'react';
import { RefreshCw,  Droplets, Scissors, AlertCircle, Info } from 'lucide-react';

// --- LEVEL DATA ---

const LEVELS = [
  {
    id: 0,
    name: "The Trap",
    description: "A classic example where a greedy choice blocks the optimal path.",
    nodes: [
      { id: 's', x: 100, y: 250, label: 's', type: 'source' },
      { id: 'a', x: 300, y: 100, label: 'a', type: 'node' },
      { id: 'b', x: 300, y: 400, label: 'b', type: 'node' },
      { id: 'c', x: 500, y: 100, label: 'c', type: 'node' },
      { id: 'd', x: 500, y: 400, label: 'd', type: 'node' },
      { id: 't', x: 700, y: 250, label: 't', type: 'sink' },
    ],
    edges: [
      { u: 's', v: 'a', cap: 10, flow: 0 },
      { u: 's', v: 'b', cap: 10, flow: 0 },
      { u: 'a', v: 'b', cap: 2,  flow: 0 }, // The bridge
      { u: 'a', v: 'c', cap: 4,  flow: 0 },
      { u: 'a', v: 'd', cap: 8,  flow: 0 },
      { u: 'b', v: 'd', cap: 9,  flow: 0 },
      { u: 'c', v: 't', cap: 10, flow: 0 },
      { u: 'd', v: 't', cap: 10, flow: 0 },
    ]
  },
  {
    id: 1,
    name: "Blue Diamond",
    description: "A multi-layered graph with vertical flows. Max Flow = 5.",
    nodes: [
      { id: 's', x: 50, y: 250, label: 's', type: 'source' },
      { id: 'u1', x: 250, y: 100, label: 'u1', type: 'node' },
      { id: 'u2', x: 250, y: 250, label: 'u2', type: 'node' },
      { id: 'u3', x: 250, y: 400, label: 'u3', type: 'node' },
      { id: 'v1', x: 550, y: 100, label: 'v1', type: 'node' },
      { id: 'v2', x: 550, y: 250, label: 'v2', type: 'node' },
      { id: 'v3', x: 550, y: 400, label: 'v3', type: 'node' },
      { id: 't', x: 750, y: 250, label: 't', type: 'sink' },
    ],
    edges: [
      { u: 's', v: 'u1', cap: 2, flow: 0 },
      { u: 's', v: 'u2', cap: 3, flow: 0 },
      { u: 's', v: 'u3', cap: 3, flow: 0 },
      { u: 'u1', v: 'v1', cap: 1, flow: 0 },
      { u: 'u1', v: 'u2', cap: 3, flow: 0 }, // Vertical Down
      { u: 'u2', v: 'v2', cap: 5, flow: 0 },
      { u: 'u3', v: 'v2', cap: 2, flow: 0 }, // Diagonal Up
      { u: 'u3', v: 'v3', cap: 2, flow: 0 },
      { u: 'v2', v: 'v1', cap: 2, flow: 0 }, // Vertical Up
      { u: 'v1', v: 't', cap: 3, flow: 0 },
      { u: 'v3', v: 't', cap: 3, flow: 0 },
    ]
  },
  {
    id: 2,
    name: "Complex Bridge",
    description: "Heavier capacities with intricate cross-connections. Max Flow = 19.",
    nodes: [
      { id: 's', x: 50, y: 250, label: 's', type: 'source' },
      { id: 'u1', x: 300, y: 100, label: 'u1', type: 'node' },
      { id: 'u2', x: 300, y: 400, label: 'u2', type: 'node' },
      { id: 'v1', x: 500, y: 100, label: 'v1', type: 'node' },
      { id: 'v2', x: 500, y: 400, label: 'v2', type: 'node' },
      { id: 't', x: 750, y: 250, label: 't', type: 'sink' },
    ],
    edges: [
      { u: 's', v: 'u1', cap: 10, flow: 0 },
      { u: 's', v: 'u2', cap: 10, flow: 0 },
      { u: 'u1', v: 'v1', cap: 4, flow: 0 },
      { u: 'u1', v: 'u2', cap: 2, flow: 0 }, // Vertical Down
      { u: 'u1', v: 'v2', cap: 8, flow: 0 }, // Diagonal
      { u: 'u2', v: 'v2', cap: 9, flow: 0 },
      { u: 'v2', v: 'v1', cap: 6, flow: 0 }, // Vertical Up
      { u: 'v1', v: 't', cap: 10, flow: 0 },
      { u: 'v2', v: 't', cap: 10, flow: 0 },
    ]
  }
];

// --- HELPER FUNCTIONS ---

const getEdge = (edges, u, v) => edges.find(e => e.u === u && e.v === v);

// Returns residual capacity from u -> v
const getResidual = (edges, u, v) => {
  const forward = getEdge(edges, u, v);
  if (forward) return forward.cap - forward.flow;

  const backward = getEdge(edges, v, u);
  if (backward) return backward.flow; // We can push back flow

  return 0;
};

// BFS to check if Max Flow is reached (finding any path in residual graph)
const hasAugmentingPath = (nodes, edges) => {
  const queue = ['s'];
  const visited = new Set(['s']);
  while(queue.length > 0) {
    const curr = queue.shift();
    if (curr === 't') return true;

    for (const nextNode of nodes) {
      if (!visited.has(nextNode.id) && getResidual(edges, curr, nextNode.id) > 0) {
        visited.add(nextNode.id);
        queue.push(nextNode.id);
      }
    }
  }
  return false;
};

// Calculate total flow leaving s
const calculateTotalFlow = (edges) => {
  return edges
    .filter(e => e.u === 's')
    .reduce((acc, e) => acc + e.flow, 0);
};

// --- COMPONENTS ---

export default function App() {
  const [levelIdx, setLevelIdx] = useState(0);
  const [nodes, setNodes] = useState(LEVELS[0].nodes);
  const [edges, setEdges] = useState(LEVELS[0].edges);

  const [path, setPath] = useState(['s']); // Current user trace
  const [msg, setMsg] = useState("Start by clicking 's' to trace a path to 't'.");
  const [phase, setPhase] = useState('FLOW'); // 'FLOW' | 'ANIMATING' | 'MINCUT' | 'VICTORY'
  const [selectedSet, setSelectedSet] = useState(new Set(['s'])); // For Min Cut phase
  const [lastAugment, setLastAugment] = useState(null); // For animation
  const [flowVal, setFlowVal] = useState(0);

  // Load level data when level changes
  const switchLevel = (idx) => {
    setLevelIdx(idx);
    setNodes(LEVELS[idx].nodes);
    // Deep copy edges to ensure fresh start
    setEdges(LEVELS[idx].edges.map(e => ({...e, flow: 0})));
    setPath(['s']);
    setPhase('FLOW');
    setMsg("Start by clicking 's' to trace a path to 't'.");
    setSelectedSet(new Set(['s']));
    setFlowVal(0);
    setLastAugment(null);
  };

  // Update total flow whenever edges change
  useEffect(() => {
    setFlowVal(calculateTotalFlow(edges));
  }, [edges]);

  // Check for Max Flow condition
  useEffect(() => {
    if (phase === 'FLOW' && !hasAugmentingPath(nodes, edges)) {
      // Small delay to let the user appreciate the last flow
      setTimeout(() => {
        setPhase('MINCUT');
        setMsg("MAX FLOW REACHED! Now, identify the Minimum Cut.");
      }, 1500);
    }
  }, [edges, phase, nodes]);

  // --- ACTIONS ---

  const handleNodeClick = (nodeId) => {
    if (phase !== 'FLOW') return;

    const lastNode = path[path.length - 1];

    // If clicking the start again, reset path
    if (nodeId === 's' && path.length > 1) {
      setPath(['s']);
      setMsg("Path reset.");
      return;
    }

    // If checking validity of step
    if (getResidual(edges, lastNode, nodeId) > 0) {
      // Prevent cycles in current path
      if (path.includes(nodeId)) return;

      const newPath = [...path, nodeId];
      setPath(newPath);

      if (nodeId === 't') {
        setMsg("Path complete! Click 'Augment Flow' to push water.");
      } else {
        setMsg(`Path: ${newPath.join(' -> ')}... keep going!`);
      }
    } else {
        // Invalid move
        // Check if they are trying to jump non-neighbors
        setMsg("Cannot flow there! No residual capacity or no pipe connection.");
    }
  };

  const handleAugment = () => {
    if (path[path.length - 1] !== 't') return;

    // 1. Find bottleneck
    let bottleneck = Infinity;
    for (let i = 0; i < path.length - 1; i++) {
      const u = path[i];
      const v = path[i+1];
      const r = getResidual(edges, u, v);
      bottleneck = Math.min(bottleneck, r);
    }

    setMsg(`Augmenting flow by ${bottleneck} units...`);
    setPhase('ANIMATING');
    setLastAugment({ path, amount: bottleneck });

    // 2. Update edges (Wait for animation visually)
    setTimeout(() => {
      const newEdges = [...edges];
      for (let i = 0; i < path.length - 1; i++) {
        const u = path[i];
        const v = path[i+1];

        // Is it a forward edge?
        const forward = newEdges.find(e => e.u === u && e.v === v);
        if (forward) {
          forward.flow += bottleneck;
        } else {
          // Must be backward edge
          const backward = newEdges.find(e => e.u === v && e.v === u);
          if (backward) {
            backward.flow -= bottleneck;
          }
        }
      }
      setEdges(newEdges);
      setPath(['s']);
      setPhase('FLOW');
      setMsg("Flow updated. Look for more paths!");
    }, 1500);
  };

  const handleMinCutClick = (nodeId) => {
    if (phase !== 'MINCUT' && phase !== 'VICTORY') return;

    const newSet = new Set(selectedSet);
    if (newSet.has(nodeId)) {
        if(nodeId !== 's') newSet.delete(nodeId); // Cannot unselect Source
    } else {
        newSet.add(nodeId);
    }
    setSelectedSet(newSet);
  };

  const verifyMinCut = () => {
    // Calculate capacity of cut: Edges (u,v) where u in Set, v not in Set
    let cutCapacity = 0;
    let cutEdges = [];

    edges.forEach(e => {
      if (selectedSet.has(e.u) && !selectedSet.has(e.v)) {
        cutCapacity += e.cap;
        cutEdges.push(e);
      }
    });

    if (cutCapacity === flowVal) {
      setPhase('VICTORY');
      setMsg(`CORRECT! Max Flow (${flowVal}) = Min Cut Capacity (${cutCapacity}). Theorem Proven!`);
    } else {
      setMsg(`Incorrect. Your cut capacity is ${cutCapacity}, but Max Flow is ${flowVal}. Hint: Select all nodes reachable from s in the residual graph.`);
    }
  };

  const resetGame = () => {
    switchLevel(levelIdx);
  };

  // --- RENDER HELPERS ---

  const renderPipe = (edge) => {
    const start = nodes.find(n => n.id === edge.u);
    const end = nodes.find(n => n.id === edge.v);

    if (!start || !end) return null;

    // Determine visual thickness based on Capacity
    const strokeWidth = 6 + (edge.cap * 1.5);

    // Calculate Flow Width (percentage of capacity)
    const flowPercentage = edge.flow / edge.cap;
    const flowWidth = strokeWidth * flowPercentage;

    // Check if this edge is part of the current trace
    const isTraced =
        path.length > 1 &&
        path.some((node, i) =>
            i < path.length - 1 &&
            ((path[i] === edge.u && path[i+1] === edge.v) || (path[i] === edge.v && path[i+1] === edge.u))
        );

    const isAnimating = phase === 'ANIMATING' && lastAugment &&
        lastAugment.path.some((n, i) =>
            i < lastAugment.path.length - 1 &&
            ((lastAugment.path[i] === edge.u && lastAugment.path[i+1] === edge.v) ||
             (lastAugment.path[i] === edge.v && lastAugment.path[i+1] === edge.u))
        );

    const isCutEdge = (phase === 'MINCUT' || phase === 'VICTORY') &&
                      selectedSet.has(edge.u) && !selectedSet.has(edge.v);

    // --- MATH FOR LABELS & ARROWS ---
    // We want them ON the line, but offset longitudinally so they don't overlap.
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const degree = angle * 180 / Math.PI;
    const midX = (start.x + end.x) / 2;
    const midY = (start.y + end.y) / 2;

    // Shift distance along the line (parallel)
    const shift = 24; // pixels to shift from center

    // Place label slightly "behind" center (towards source node of edge)
    const labelX = midX - shift * Math.cos(angle);
    const labelY = midY - shift * Math.sin(angle);

    // Place arrow slightly "ahead" of center (towards target node of edge)
    const arrowX = midX + shift * Math.cos(angle);
    const arrowY = midY + shift * Math.sin(angle);

    return (
      <g key={`${edge.u}-${edge.v}`} className="group cursor-pointer">
        {/* Background Pipe (Capacity) */}
        <line
          x1={start.x} y1={start.y} x2={end.x} y2={end.y}
          stroke={isCutEdge ? "#ef4444" : "#cbd5e1"}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="transition-colors duration-300"
        />

        {/* Inner Water (Flow) */}
        <line
          x1={start.x} y1={start.y} x2={end.x} y2={end.y}
          stroke="#3b82f6" // Blue
          strokeWidth={Math.max(0, flowWidth)}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />

        {/* Animation Overlay (Dashed moving line) */}
        {(isTraced || isAnimating) && (
          <line
            x1={start.x} y1={start.y} x2={end.x} y2={end.y}
            stroke={isAnimating ? "#60a5fa" : "#f59e0b"}
            strokeWidth={isAnimating ? strokeWidth : 4}
            strokeDasharray="10,10"
            className={isAnimating ? "animate-flow" : ""}
            opacity={0.8}
          />
        )}

        {/* Label: Flow / Cap */}
        {/* We center it at labelX/labelY */}
        <foreignObject
          x={labelX - 20}
          y={labelY - 10}
          width="40" height="20"
          style={{ pointerEvents: 'none' }}
        >
          <div className="flex items-center justify-center h-full">
             <span className={`text-xs font-bold px-1 rounded shadow-sm ${isCutEdge ? 'bg-red-100 text-red-800' : 'bg-white/90 text-slate-800'}`}>
               {edge.flow}/{edge.cap}
             </span>
          </div>
        </foreignObject>

        {/* Arrow Head */}
        {/* Fixed transformation: Define shape around 0,0, then Translate -> Rotate -> Scale */}
        <polygon
             points="-4,-4 4,0 -4,4"
             fill="#475569"
             transform={`translate(${arrowX}, ${arrowY}) rotate(${degree}) scale(1.5)`}
             className="drop-shadow-sm pointer-events-none"
        />
      </g>
    );
  };

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto p-4 bg-slate-50 min-h-screen font-sans">

      {/* Header & Level Select */}
      <div className="mb-4 text-center w-full">
        <h1 className="text-3xl font-bold text-slate-800 flex items-center justify-center gap-2 mb-4">
          <Droplets className="text-blue-500" /> Max Flow Min Cut Challenge
        </h1>

        {/* Level Tabs */}
        <div className="flex justify-center gap-2 mb-4">
            {LEVELS.map((lvl, idx) => (
                <button
                    key={lvl.id}
                    onClick={() => switchLevel(idx)}
                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                        levelIdx === idx 
                        ? 'bg-blue-600 text-white shadow-md scale-105' 
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                >
                    {lvl.name}
                </button>
            ))}
        </div>

        <p className="text-slate-600 text-sm max-w-xl mx-auto">
          <span className="font-bold text-slate-800">{LEVELS[levelIdx].name}:</span> {LEVELS[levelIdx].description}
        </p>
      </div>

      {/* Game Stats Bar */}
      <div className="w-full bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex justify-between items-center mb-4">
        <div className="flex gap-8">
          <div>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Current Flow</span>
            <div className="text-2xl font-bold text-blue-600">{flowVal} Units</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Phase</span>
            <div className={`text-2xl font-bold ${phase === 'VICTORY' ? 'text-green-500' : 'text-purple-600'}`}>
              {phase === 'FLOW' ? 'Augmentation' : phase === 'ANIMATING' ? 'Flowing...' : phase === 'MINCUT' ? 'Min-Cut Analysis' : 'Theorem Proven!'}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={resetGame}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <RefreshCw size={18} /> Reset Level
          </button>
        </div>
      </div>

      {/* Main Canvas Area */}
      <div className="relative bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden p-4">
        {/* Status Message Overlay */}
        <div className={`absolute top-4 left-0 right-0 mx-auto w-max px-6 py-2 rounded-full text-sm font-semibold shadow-sm z-10 transition-all
          ${phase === 'VICTORY' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-blue-50 text-blue-800 border border-blue-100'}
        `}>
           {msg}
        </div>

        <svg width="800" height="500" viewBox="0 0 800 500" className="mx-auto select-none">
            <defs>
                <style>
                    {`
                        @keyframes flow {
                            to { stroke-dashoffset: -20; }
                        }
                        .animate-flow {
                            animation: flow 0.5s linear infinite;
                        }
                    `}
                </style>
            </defs>

          {/* Edges */}
          {edges.map(renderPipe)}

          {/* Nodes */}
          {nodes.map(node => {
             const isSelected = path.includes(node.id);
             const isLast = path[path.length-1] === node.id;
             const isInSSet = selectedSet.has(node.id);

             // Determine visual state based on phase
             let ringColor = "stroke-slate-300";
             let fillColor = "fill-slate-50";

             if (phase === 'FLOW') {
                if (isSelected) {
                    ringColor = "stroke-blue-500";
                    fillColor = isLast ? "fill-blue-100" : "fill-slate-50";
                }
             } else if (phase === 'MINCUT' || phase === 'VICTORY') {
                 if (isInSSet) {
                     ringColor = "stroke-green-500";
                     fillColor = "fill-green-50";
                 }
             }

             return (
               <g
                 key={node.id}
                 onClick={() => phase === 'FLOW' ? handleNodeClick(node.id) : handleMinCutClick(node.id)}
                 className="cursor-pointer hover:opacity-90 transition-opacity"
               >
                 <circle
                   cx={node.x} cy={node.y} r={30}
                   className={`${fillColor} ${ringColor} transition-colors duration-300`}
                   strokeWidth="4"
                 />
                 <text
                   x={node.x} y={node.y} dy=".3em" textAnchor="middle"
                   className="font-bold text-slate-700 pointer-events-none text-lg"
                 >
                   {node.label}
                 </text>
                 {/* Hover ring for easier clicking */}
                 <circle cx={node.x} cy={node.y} r={40} fill="transparent" />
               </g>
             )
          })}
        </svg>

        {/* Interactive Controls */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4 pointer-events-none">

          {/* AUGMENT BUTTON */}
          {phase === 'FLOW' && path[path.length-1] === 't' && (
            <button
              onClick={handleAugment}
              className="pointer-events-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-bold shadow-lg transform transition-all hover:scale-105 animate-bounce"
            >
              <Droplets size={20} /> Augment Flow
            </button>
          )}

          {/* VERIFY MIN CUT BUTTON */}
          {phase === 'MINCUT' && (
            <button
              onClick={verifyMinCut}
              className="pointer-events-auto flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-bold shadow-lg transform transition-all hover:scale-105"
            >
              <Scissors size={20} /> Verify Cut
            </button>
          )}
        </div>
      </div>

      {/* Legend / Instructions Footer */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-3xl text-sm text-slate-600">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
            <h3 className="font-bold mb-2 flex items-center gap-2"><Info size={16}/> How to Play</h3>
            <ul className="list-disc pl-4 space-y-1">
                <li>Click nodes to connect <strong>s</strong> to <strong>t</strong>.</li>
                <li>Grey thickness = Pipe Capacity. Blue = Water Flow.</li>
                <li>You can use edges with residual capacity.</li>
                <li><strong>Advanced:</strong> If a pipe flows $a \to b$, you can flow $b \to a$ to "push back" water!</li>
            </ul>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
            <h3 className="font-bold mb-2 flex items-center gap-2"><AlertCircle size={16}/> Min-Cut Phase</h3>
            <ul className="list-disc pl-4 space-y-1">
                <li>When no more paths exist, the game pauses.</li>
                <li>Select all nodes you can still reach from <strong>s</strong>.</li>
                <li>The edges leaving your selection form the <strong>Minimum Cut</strong>.</li>
            </ul>
        </div>
      </div>
    </div>
  );
}